import * as React from 'react';

import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Group, Stack, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import clamp from 'lodash/clamp';

import cloudUploadIcon from '../assets/icon-cloud-upload.svg';
import helpIcon from '../assets/icon-help-circle.svg';
import type { BodyUploadFileApiStellarUploadPost } from '../generated/generatedOpenAPI';
import { formatAntibodyValidationMessages } from '../overview/experimentStatusMessageFormatter';
import { useAppDispatch } from '../store/hooks';
import { uploadCleared, uploadFailed, uploadProgress, uploadStarted, uploadStepChanged, uploadValidationResult } from '../store/slices/uploads.slice';
import { useDeleteFile, useLazyGetValidationResult, useUploadFile, useValidateAntibodyFile } from '../store/store';

export interface UploadProps {
  experimentId: string;
  label?: string;
  description?: string;
  fileTypes: string[];
  subtitle: string;
  onFileChange: (fileLocation: string) => void;
  required?: boolean;
  fileName?: string | null; // optional prop to set the file name, if it is already known (e.g. when going back from step 3 to step 2 in the CreateExperimentsModal)
  formError?: string;
  checkHeaderIncludes?: string[]; // optional prop to check if the header includes specific strings
  onFileDrop?: () => Promise<void>; // called when a file is dropped (e.g. to initialize the experiment on the server)
  helpText?: string;
}

enum UploadStatus {
  Idle = 'idle',
  Error = 'error',
  Success = 'success',
}

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}

export function FileUpload({
  experimentId,
  label,
  description,
  fileTypes,
  subtitle,
  onFileChange,
  required,
  formError,
  checkHeaderIncludes,
  onFileDrop,
  fileName: uploadedFileName,
  helpText,
}: UploadProps) {
  const theme = useMantineTheme();
  const dispatch = useAppDispatch();
  const [file, setFile] = React.useState<File | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [status, setStatus] = React.useState<UploadStatus>(UploadStatus.Idle);
  const strippedFileName = uploadedFileName ? (uploadedFileName.split('/').pop() ?? null) : null;

  // const [uniqueFileName, setUniqueFileName] = React.useState<string>(uuidv4());
  // store a reference to the unique file name so that it is not reset on each render
  const fileNameRef = React.useRef<string | null>(strippedFileName);
  const fileName = fileNameRef.current;
  const setFileName = React.useCallback((value: string | null) => {
    fileNameRef.current = value;
  }, []);

  const [inputInstanceKey, setInputInstanceKey] = React.useState<number>(Date.now());

  const [uploadChunk] = useUploadFile();
  const [deleteFileFromServer] = useDeleteFile();
  const [validateAntibodyFile] = useValidateAntibodyFile();
  const [getValidationResult] = useLazyGetValidationResult();

  const dropzoneAccept = React.useMemo(() => {
    return {
      [MIME_TYPES.csv]: fileTypes.map((ext) => `.${ext}`),
    };
  }, [fileTypes]);

  const handleAddFile = React.useCallback((selectedFile: File | null) => {
    setFile(selectedFile);
    setInputInstanceKey(Date.now());
  }, []);

  const clearLocalFileState = React.useCallback(() => {
    handleAddFile(null);
    setFileName(null);
    onFileChange('');
  }, [handleAddFile, onFileChange, setFileName]);

  const handleResetFile = React.useCallback(async () => {
    setIsDeleting(true);
    setLoading(false);
    setStatus(UploadStatus.Idle);
    clearLocalFileState();
    dispatch(uploadCleared({ experimentId }));
    if (fileName) {
      try {
        await deleteFileFromServer({ experimentId, fileName });
      } catch (error) {
        console.error('Error deleting file from server', error);
      }
    }
    setIsDeleting(false);
  }, [clearLocalFileState, deleteFileFromServer, dispatch, experimentId, fileName]);

  const handleClickDeleteFile = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      await handleResetFile();
    },
    [handleResetFile],
  );

  const handleDrop = React.useCallback(
    async (files: File[]) => {
      setLoading(true);
      setStatus(UploadStatus.Idle);
      handleAddFile(null);

      const uploadedFile = files[0]; // only one file is allowed to be uploaded (multiple={false})
      setFileName(uploadedFile?.name ?? null);

      console.debug('File is', uploadedFile);
      if (!uploadedFile) {
        dispatch(uploadFailed({ experimentId, error: 'No file was uploaded.' }));
        setLoading(false);
        return;
      }
      dispatch(uploadStarted({ experimentId }));
      handleAddFile(uploadedFile);

      const fileType = uploadedFile.name.split('.').pop();
      if (!fileTypes.includes(fileType ?? '')) {
        const errorText = `Invalid file format. Expected .${fileTypes.join(', .')}, got .${fileType ?? 'unknown'}`;
        setStatus(UploadStatus.Error);
        dispatch(uploadFailed({ experimentId, error: errorText }));
        clearLocalFileState();
        setLoading(false);
        return;
      }

      if (onFileDrop) {
        try {
          await onFileDrop();
        } catch (error) {
          console.error('onFileDrop failed', error);
          dispatch(uploadFailed({ experimentId, error: 'Failed to initialize experiment.' }));
          clearLocalFileState();
          setLoading(false);
          return;
        }
      }

      dispatch(uploadStepChanged({ experimentId, phase: 'uploading' }));

      // chunk size so that there are at least 10 chunks
      // except if chunk size is below smallestChunkSizeInMB, then we use smallestChunkSizeInMB
      const smallestChunkSizeInMB = 64;
      const minChunkSize = smallestChunkSizeInMB * 1024 ** 2;
      // or if chunk size is above 1GB, then we use 1GB
      const maxChunkSize = 1024 ** 3;
      const chunkSize = Math.ceil(clamp(uploadedFile.size / 10, minChunkSize, maxChunkSize));
      console.debug('Chunk size is', chunkSize, 'bytes');

      let start = 0;
      let chunkNo = 0;
      console.time('upload');
      try {
        while (start < uploadedFile.size) {
          const chunk = uploadedFile.slice(start, start + chunkSize, 'application/octet-stream');
          const lastChunk = start + chunkSize >= uploadedFile.size;

          const formData = new FormData();
          formData.append('experiment_id', experimentId);
          formData.append('file_name', uploadedFile.name);
          formData.append('file_chunk', chunk);
          formData.append('chunk_no', chunkNo.toString());
          formData.append('last_chunk', lastChunk.toString());
          console.debug('Sending chunk with unique file name', uploadedFile.name, 'and chunk number', chunkNo);

          if (checkHeaderIncludes) {
            checkHeaderIncludes.forEach((item) => {
              formData.append('check_header_includes', item);
            });
          }

          // Here we want to wait in each loop to transmit the chunks sequentially (less concurrent requests and writes to disk, and the last chunk is the last to arrive at the server so that the file can be reconstructed)
          // eslint-disable-next-line no-await-in-loop
          const result = await uploadChunk({
            // file needs to be sent via FormData, but the generated OpenAPI client expects a different type
            bodyUploadFileApiStellarUploadPost: formData as unknown as BodyUploadFileApiStellarUploadPost,
          }).unwrap(); // unwrap throws an error if the promise is rejected
          console.debug('Upload successful:', result);

          const { fileLocation } = result;
          if (fileLocation) {
            console.debug('File location:', fileLocation);
            onFileChange(fileLocation);
          }
          // no error -> success
          start += chunkSize;
          chunkNo += 1;

          const completedBytes = Math.min(start, uploadedFile.size);
          const progress = uploadedFile.size > 0 ? Math.min(100, Math.round((completedBytes / uploadedFile.size) * 100)) : 100;
          dispatch(uploadProgress({ experimentId, progress }));
        }
      } catch (error) {
        console.error('Error uploading file', error);
        let errorMessage = 'File could not be uploaded.';
        if (isFetchBaseQueryError(error)) {
          if (typeof error.data === 'object' && error.data != null && 'detail' in error.data) {
            errorMessage = (error.data as { detail: string }).detail;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        dispatch(uploadFailed({ experimentId, error: errorMessage }));
        setStatus(UploadStatus.Error);
        try {
          if (uploadedFile?.name) {
            await deleteFileFromServer({ experimentId, fileName: uploadedFile.name });
          }
        } catch (cleanupError) {
          console.error('Error cleaning up file', cleanupError);
        }
        clearLocalFileState();
        setLoading(false);
        console.timeEnd('upload');
        return;
      }

      dispatch(uploadProgress({ experimentId, progress: 100 }));

      dispatch(uploadStepChanged({ experimentId, phase: 'validating' }));
      try {
        // will throw error that exits try block if status != 200 status code
        await validateAntibodyFile({ experimentId, fileName: uploadedFile.name }).unwrap();
        // Force a fresh request (preferCacheValue=false) to avoid stale RTK Query cache on re-upload
        const validationResult = await getValidationResult({ experimentId, fileName: uploadedFile.name }, false).unwrap();

        const validationMessage = formatAntibodyValidationMessages(validationResult, {});
        // Convert ProcessedAntibodyCsvValidationModel to ValidationResult
        const { severity: validationSeverity, message: validationMessageText } = validationMessage.short[0]
          ? validationMessage.short[0]
          : {
              severity: 'error',
              message: 'Failed to generate validation short message',
            };
        if (validationSeverity !== 'error') {
          // For warnings: use individual detailed messages when multiple exist
          const warningDetailMessages = validationMessage.detailed.filter((msg) => msg.severity === 'warning').map((msg) => msg.message);
          let issues: string[];
          if (validationSeverity === 'info') {
            issues = [];
          } else if (warningDetailMessages?.length > 1) {
            issues = warningDetailMessages;
          } else {
            issues = [validationMessageText];
          }

          const mappedValidationResult = {
            issues,
            isValid: true,
            lineCount: validationResult.lineCount,
            fileRows: validationResult.fileRows,
            missingColumns: validationResult.missingColumns?.length ? validationResult.missingColumns : undefined,
          };
          dispatch(
            uploadValidationResult({
              experimentId,
              validation: mappedValidationResult,
            }),
          );
          setStatus(UploadStatus.Success);
        } else {
          // For errors: pass individual detailed messages so the UI can show a bulleted list
          const errorDetailMessages = validationMessage.detailed.filter((msg) => msg.severity === 'error').map((msg) => msg.message);

          dispatch(
            uploadFailed({
              experimentId,
              error: validationMessageText,
              errorDetails: errorDetailMessages.length > 1 ? errorDetailMessages : undefined,
            }),
          );
          setStatus(UploadStatus.Error);
        }
      } catch (validationError) {
        console.log('Error validating file.', validationError);
        let validationMessage = 'File could not be validated.';
        if (isFetchBaseQueryError(validationError)) {
          if (typeof validationError.data === 'object' && validationError.data != null && 'detail' in validationError.data) {
            validationMessage = (validationError.data as { detail: string }).detail;
          }
        } else if (validationError instanceof Error) {
          validationMessage = validationError.message;
        }

        dispatch(uploadFailed({ experimentId, error: validationMessage }));
        setStatus(UploadStatus.Error);
      }
      setLoading(false);
      console.timeEnd('upload');
    },
    [
      checkHeaderIncludes,
      clearLocalFileState,
      deleteFileFromServer,
      dispatch,
      experimentId,
      fileTypes,
      handleAddFile,
      onFileChange,
      onFileDrop,
      setFileName,
      validateAntibodyFile,
      getValidationResult,
      uploadChunk,
    ],
  );

  // Dropzone's accepted file types are determined by the `accept` prop
  // it can narrow down the accepted file types by MIME Type
  // mime types: determine in ubuntu with `file --mime-type FILENAME`
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
  // nd2 files are application/octet-stream, tif files are image/tiff
  // however, the nd2 filter did not work when files are dropped (i.e. DropZone.Reject was shown)
  // so we need to check the file extension in the handleDrop function

  return (
    <Stack gap={3} w="100%">
      {label && (
        <Group gap={3}>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {required && (
            <Text size="sm" component="span" c="red" inherit>
              *
            </Text>
          )}
          {helpText && (
            <Tooltip
              label={helpText}
              position="right"
              withArrow
              arrowSize={10}
              withinPortal
              portalProps={{ style: { position: 'absolute', zIndex: 10001 } }}
              classNames={{ tooltip: 'help-tooltip', arrow: 'help-tooltip-arrow' }}
            >
              <img src={helpIcon} alt="Help" className="w-4 h-4 cursor-pointer" />
            </Tooltip>
          )}
        </Group>
      )}
      {description ? (
        <Text c="dimmed" size="xs">
          {description}
        </Text>
      ) : null}
      <Dropzone
        loading={loading || isDeleting}
        onDrop={handleDrop}
        onReject={(fileRejections) => {
          const rejectedExt = fileRejections[0]?.file?.name?.split('.').pop() ?? 'unknown';
          dispatch(uploadStarted({ experimentId }));
          dispatch(
            uploadFailed({
              experimentId,
              error: `Invalid file format. Expected .${fileTypes.join(', .')}, got .${rejectedExt}`,
            }),
          );
        }}
        multiple={false}
        data-testid="upload-dropzone"
        key={inputInstanceKey}
        accept={dropzoneAccept}
      >
        <Stack align="center" gap={6} w="100%">
          <img src={cloudUploadIcon} alt="Upload" width={32} height={32} />
          {file || strippedFileName ? (
            <Group gap="xs">
              <Text size="sm" fw={500} data-testid="upload-file-label">
                {file ? file.name : strippedFileName}
              </Text>
              <ActionIcon size="sm" variant="subtle" onClick={handleClickDeleteFile} style={{ pointerEvents: 'all' }} color="red">
                <FontAwesomeIcon icon={faTrash} size="xs" />
              </ActionIcon>
            </Group>
          ) : (
            <>
              <Text size="sm" fw={500} data-testid="upload-file-label">
                Upload File
              </Text>
              <Text size="xs" c={theme.colors.gray[6]}>
                {subtitle}
              </Text>
            </>
          )}
        </Stack>
      </Dropzone>
    </Stack>
  );
}
